import React, { useState, useEffect } from 'react';
import {
    collection,
    getDocs,
    doc,
    updateDoc,
    query,
    orderBy
} from 'firebase/firestore';
import { db, auth } from './firebase';
import {
    UsersIcon,
    CheckBadgeIcon,
    NoSymbolIcon,
    ShieldExclamationIcon,
    MagnifyingGlassIcon,
    UserCircleIcon
} from '@heroicons/react/24/outline';

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); // all, paid, pending
    const isAdmin = auth.currentUser?.email === process.env.REACT_APP_ADMIN_EMAIL;

    useEffect(() => {
        const fetchUsers = async () => {
            if (!isAdmin) return;
            try {
                const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
                const querySnapshot = await getDocs(q);
                const usersList = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setUsers(usersList);
            } catch (err) {
                console.error("Erro ao buscar usuários:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, [isAdmin]);

    const handleToggleAccess = async (userId, currentStatus) => {
        try {
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, {
                isPaid: !currentStatus
            });
            // Atualiza o estado local
            setUsers(users.map(u => u.id === userId ? { ...u, isPaid: !currentStatus } : u));
        } catch (err) {
            alert("Erro ao atualizar acesso.");
        }
    };

    if (!isAdmin) {
        return (
            <div className="flex flex-col items-center justify-center py-20 animate-fade-in text-center">
                <ShieldExclamationIcon className="w-20 h-20 text-red-500/50 mb-6" />
                <h2 className="text-3xl font-black text-white italic uppercase mb-2">Acesso Restrito</h2>
                <p className="text-gray-500 max-w-sm">Esta área é exclusiva para o administrador mestre da TraderFlix.</p>
            </div>
        );
    }

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.name?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterStatus === 'all' ||
            (filterStatus === 'paid' && user.isPaid) ||
            (filterStatus === 'pending' && !user.isPaid);
        return matchesSearch && matchesFilter;
    });

    return (
        <div className="space-y-8 animate-fade-in pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-black text-white italic uppercase tracking-tighter">Gerenciar Alunos</h1>
                    <p className="text-gray-500 font-medium tracking-wide">Controle de acesso e ativação de assinaturas.</p>
                </div>
                <div className="flex items-center gap-4 bg-emerald-500/10 border border-emerald-500/20 px-6 py-4 rounded-3xl">
                    <UsersIcon className="w-8 h-8 text-emerald-500" />
                    <div>
                        <p className="text-[10px] font-black text-emerald-500/70 uppercase">Total de Membros</p>
                        <p className="text-2xl font-black text-white">{users.length}</p>
                    </div>
                </div>
            </div>

            {/* Filters & Search */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 relative">
                    <MagnifyingGlassIcon className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                        type="text"
                        placeholder="Pesquisar por nome ou e-mail..."
                        className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-14 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30 transition-all font-medium"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setFilterStatus('all')}
                        className={`flex-1 py-4 rounded-2xl font-black text-xs uppercase transition-all ${filterStatus === 'all' ? 'bg-emerald-600 text-white' : 'bg-white/5 text-gray-400 border border-white/5'}`}
                    >
                        Todos
                    </button>
                    <button
                        onClick={() => setFilterStatus('pending')}
                        className={`flex-1 py-4 rounded-2xl font-black text-xs uppercase transition-all ${filterStatus === 'pending' ? 'bg-amber-500 text-black' : 'bg-white/5 text-gray-400 border border-white/5'}`}
                    >
                        Pendentes
                    </button>
                </div>
            </div>

            {/* Users Table / Grid */}
            <div className="grid grid-cols-1 gap-4">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 animate-pulse">
                        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                        <p className="text-gray-500 uppercase font-black text-xs tracking-widest">Sincronizando banco de dados...</p>
                    </div>
                ) : filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                        <div key={user.id} className="bg-white/[0.03] border border-white/5 rounded-3xl p-6 flex flex-col md:flex-row items-center gap-6 hover:bg-white/[0.05] transition-all group">
                            <div className="w-14 h-14 bg-gradient-to-br from-emerald-500/10 to-blue-500/10 border border-white/10 rounded-2xl flex items-center justify-center">
                                <UserCircleIcon className="w-8 h-8 text-emerald-500/50" />
                            </div>

                            <div className="flex-1 text-center md:text-left overflow-hidden">
                                <h3 className="font-black text-white italic uppercase tracking-tight truncate">{user.name || 'Usuário Sem Nome'}</h3>
                                <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-1">
                                    <p className="text-xs text-gray-500 font-bold truncate">{user.email}</p>
                                    <p className="text-xs text-gray-400 font-bold tracking-tight">CPF: {user.cpf || '---'}</p>
                                </div>
                            </div>

                            <div className="flex flex-col md:flex-row items-center gap-4">
                                <div className={`px-4 py-2 rounded-xl border flex items-center gap-2 ${user.isPaid
                                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500'
                                    : 'bg-amber-500/10 border-amber-500/30 text-amber-500'
                                    }`}>
                                    <div className={`w-2 h-2 rounded-full ${user.isPaid ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`}></div>
                                    <span className="text-[10px] font-black uppercase tracking-widest">
                                        {user.isPaid ? 'Acesso VIP Ativo' : 'Aguardando Pagamento'}
                                    </span>
                                </div>

                                <button
                                    onClick={() => handleToggleAccess(user.id, user.isPaid)}
                                    className={`px-6 py-3 rounded-xl font-black text-xs uppercase tracking-tighter transition-all flex items-center gap-2 ${user.isPaid
                                        ? 'bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white'
                                        : 'bg-emerald-600 text-white hover:bg-emerald-500'
                                        }`}
                                >
                                    {user.isPaid ? (
                                        <> <NoSymbolIcon className="w-4 h-4" /> Revogar Acesso </>
                                    ) : (
                                        <> <CheckBadgeIcon className="w-4 h-4" /> Liberar Acesso </>
                                    )}
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="py-20 bg-white/5 border border-dashed border-white/10 rounded-[3rem] text-center">
                        <UsersIcon className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                        <p className="text-gray-500 font-black uppercase tracking-widest">Nenhum aluno encontrado.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminUsers;
